import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import MediaModalContent from './modals/MediaModalContent';
import ImageLightboxModal from './modals/ImageLightboxModal';
import CreateEditPostModal from './modals/CreateEditPostModal';
import FilterGroupsModal from './modals/FilterGroupsModal';
import AddTopPerformanceModal from './modals/AddTopPerformanceModal';
import ReportPostModal from './modals/ReportPostModal';
import FeedModalWrapper from './modals/FeedModalWrapper';

import {
  setActiveReduxModal,
  resetSubmitSupportError,
  resetReportPostInfo,
} from './ducks/feedActions';

const MainContainer = styled('main')`
  max-height: calc(100vh - 210px);
  overflow: auto;
`;

const getModalStyles = (modalType) => {
  switch (modalType) {
    case 'create-post-modal':
    case 'edit-post-modal':
    case 'add-top-performance-modal':
    case 'filter-groups-modal':
      return {
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          overflowY: 'auto',
        },
        content: {
          borderRadius: '12px',
        },
      };

    case 'play-message-video-modal':
    case 'image-lightbox':
      return {
        content: {
          width: '50vw',
          minWidth: '530px',
          maxWidth: '680px',
          backgroundColor: '#222222',
        },
      };

    case 'report-post-modal':
      return {
        content: {
          minHeight: '400px',
          minWidth: '380px',
          width: '380px',
          borderRadius: '24px',
        },
      };

    default:
      return {};
  }
};

const ModalHandler = () => {
  const dispatch = useDispatch();
  const activeModal = useSelector((state) => state.feed.data.activeModal);
  const messageMediaUrl = useSelector(
    (state) => state.feed.data.messageMediaUrl,
  );

  const getAltMediaUrl = useCallback((mediaUrl) => {
    if (mediaUrl.includes('drive.google')) {
      const split = mediaUrl.split('/');
      const id = split[split.length - 2];
      return `https://drive.google.com/uc?export=download&id=${id}`;
    }
    return mediaUrl;
  }, []);

  const handleCloseModal = () => {
    dispatch(setActiveReduxModal(''));
    if (activeModal === 'report-post-modal') {
      dispatch(resetSubmitSupportError());
      dispatch(resetReportPostInfo());
    }
  };

  const getModalStylesConfig = useMemo(() => {
    const modalStyle = getModalStyles(activeModal);
    return {
      overlayStyles: modalStyle.overlay || {},
      contentStyles: modalStyle.content || {},
    };
  }, [activeModal]);

  const getModalConfig = useMemo(() => {
    const altMediaUrl = getAltMediaUrl(messageMediaUrl);

    return {
      'create-post-modal': {
        content: CreateEditPostModal,
        props: {},
      },
      'play-message-video-modal': {
        content: MediaModalContent,
        props: { mediaUrl: altMediaUrl },
      },
      'image-lightbox': {
        content: ImageLightboxModal,
        props: { mediaUrl: altMediaUrl },
      },
      'edit-post-modal': {
        content: CreateEditPostModal,
        props: { isEdit: true },
      },
      'filter-groups-modal': {
        content: FilterGroupsModal,
        props: {},
      },
      'add-top-performance-modal': {
        content: AddTopPerformanceModal,
        props: {},
      },
      'report-post-modal': {
        content: ReportPostModal,
        props: {},
      },
    };
  }, [getAltMediaUrl, messageMediaUrl]);

  const createModal = (config) => {
    if (!activeModal || !config) return null;

    const { content: Content, props = {} } = config;
    const { overlayStyles, contentStyles } = getModalStylesConfig;

    return (
      <FeedModalWrapper
        isOpen={!!activeModal}
        onRequestClose={handleCloseModal}
        modalName={activeModal}
        overlayStyles={overlayStyles}
        contentStyles={contentStyles}
      >
        <Content onRequestClose={handleCloseModal} {...props} />
      </FeedModalWrapper>
    );
  };

  const renderModal = () => {
    const modalConfig = getModalConfig;
    return createModal(modalConfig[activeModal]);
  };

  return <MainContainer>{renderModal()}</MainContainer>;
};

export default ModalHandler;
